// ============================================================================
// PROJECT DATA CONTEXT - Project data and API
// ============================================================================
// What it does: Holds the project list (myProjects) from the API, loading/error state,
// and all API operations: fetch (fetchFilteredProjects), add/remove/update project.
// It reads filter and tag state from ProjectFilterContext and ProjectTagContext to build
// the API request; it does not own filter criteria. The displayed list is derived via
// useDisplayProjects (normalize → tab filter) and exposed as getDisplayProjects().
// So: data + API; display derivation lives in the hook.

import React, { createContext, useState, useContext, useCallback, useMemo, useEffect } from 'react';
import axios from 'axios';
import APIURLMaker from '../../../utils/APIURLMaker';
import { useAuth } from '../../../shared/context/AuthContext';
import { useProjectTag } from './ProjectTagContext';
import { useProjectFilter } from './ProjectFilterContext';
import { PROJECT_STATUSES, getNonDeletedStatuses } from '../../../shared/constants/itemConstants';
import { transformItemFromBackend, transformItemToBackend } from '../../../utils/itemTransformers';
import { transformRecordFromBackend } from '../../../utils/transformRecordFromBackend';
import { useAccessMapNormalization } from '../../../hooks/accessMap/useAccessMapNormalization';
import { useDisplayProjects } from '../hooks/useDisplayProjects';
import { subscribeToProjectUpdates } from '../realtime/projectSseAdapter';
import { parseProjectSsePayload } from '../realtime/projectSsePayload';
import { useRealtimeFieldHighlights } from '../../../shared/realtime/useRealtimeFieldHighlights';
import { useManagedRealtimeSubscriptions } from '../../../shared/realtime/useManagedRealtimeSubscriptions';
import { handleIncomingRecordUpdate } from '../../../shared/realtime/handleIncomingRecordUpdate';
import { usePagination } from '../../../shared/hooks/usePagination';
import { useUserPrefs } from '../../../shared/hooks/useUserPrefs';
import { useFilterPresetsBootstrap } from '../../../shared/context/FilterPresetsBootstrapContext';
import { buildStatusListForRequest } from '../../../shared/filters/buildStatusListForRequest';
import { buildListScope } from '../../../shared/filters/buildListScope';
import { fetchPostWithEmptyPageRetry } from '../../../shared/pagination/fetchPostWithEmptyPageRetry';

// ============================================================================
// CONTEXT CREATION AND EXPORTS
// ============================================================================

const ProjectDataContext = createContext();

export const useProjectData = () => useContext(ProjectDataContext);

// ============================================================================
// PROVIDER COMPONENT
// ============================================================================

export const ProjectDataProvider = ({ children }) => {
  // ============================================================================
  // EXTERNAL DEPENDENCIES
  // ============================================================================
  
  // Get current user information and profile from authentication context
  const { currentUser, userProfile } = useAuth();
  
  // ============================================================================
  // ACCESS MAP HOOKS
  // ============================================================================
  
  // Use normalization hook for accessMap operations (fetch + update still use it)
  const { normalizeAccessMap } = useAccessMapNormalization(currentUser);
  
  // Get tag management from ProjectTagContext (tags are loaded on demand when Create/Edit modal opens)
  const { 
    addTags,
    markTagsAsInitialized,
    tagsInitialized,
    selectedTags
  } = useProjectTag();
  
  // Get filter management functions from ProjectFilterContext
  const {
    selectedStatuses,
    searchByKeyword,
    searchByTitle,
    searchByDescription,
    searchByTag,
    queryFromDay,
    queryToDay,
    appliedRoleFilter,
    includeMyPublicProjects,
    noRatingFilterApplied,
    selectedMinRating,
    activeTab
  } = useProjectFilter();

  // ============================================================================
  // PERMISSION AND USER STATE
  // ============================================================================
  
  // Tracks whether the current user has permission to access project features
  const [hasPermission, setHasPermission] = useState(false);
  
  // User type - currently only supports "personal" type as per requirements
  const [userType, setUserType] = useState("personal");

  // ============================================================================
  // PROJECT DATA STATE
  // ============================================================================
  // Storage policy: Main list is API-only (no merge from localStorage).
  // Recently viewed is user-scoped localStorage (IDs only) via useRecentlyViewed.
  //
  // Main list of projects fetched from the API based on current filters
  const [myProjects, setMyProjects] = useState([]);
  const {
    highlightsById: projectFieldHighlightsById,
    highlightDetailsById: projectFieldHighlightDetailsById,
    scheduleHighlight: scheduleProjectHighlight,
    clearAllHighlights: clearAllProjectHighlights,
  } = useRealtimeFieldHighlights();

  // ============================================================================
  // LOADING AND ERROR STATE
  // ============================================================================
  
  // Global loading state for project operations
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoadedListOnce, setHasLoadedListOnce] = useState(false);
  
  // Error state for displaying error messages to users
  const [error, setError] = useState(null);

  const { getPageSize } = useUserPrefs();
  const { isBootstrapReady, skipInitialListFetch } = useFilterPresetsBootstrap();

  const {
    page,
    pageSize,
    pagination,
    applyPagedMeta,
    resetPage,
  } = usePagination(getPageSize());

  // ============================================================================
  // PERMISSION CHECKING EFFECT
  // ============================================================================
  
  // Check if the current user has permission to access project features
  useEffect(() => {
    const checkPermission = () => {
      try {
        // Get user roles from session storage (set during login)
        const roles = JSON.parse(sessionStorage.getItem("roles") || "[]");
        
        // Check if user has any of the required roles for project access
        const hasAccess = roles.some((role) =>
          ["ROLE_SUPERVISOR", "ROLE_ADMIN", "ROLE_MANAGER", "ROLE_USER"].includes(role?.name)
        );
        
        // Set permission state - this controls whether project features are available
        setHasPermission(hasAccess);
        
        // Log error if user doesn't have access (for debugging)
        if (!hasAccess) {
          console.error("Permission denied");
        }
      } catch (error) {
        // Handle JSON parsing errors or other permission check failures
        console.error("Error checking permissions:", error);
        setHasPermission(false);
      }
    };

    // Run permission check when component mounts
    checkPermission();
  }, []); // Empty dependency array means this runs only once on mount

  // ============================================================================
  // MAIN PROJECT DATA FETCHING FUNCTION
  // ============================================================================
  
  // This is the primary function for fetching projects from the API with filtering
  const fetchFilteredProjects = useCallback(async (
    overrideQueryFromDay = null,
    overrideQueryToDay = null,
    overrideSearchByTitle = null,
    overrideSearchByDescription = null,
    overrideSearchByTag = null,
    {
      overrideKeyword = null,
      overrideTags = null,
      overrideStatuses = null,
      overrideMinRating = undefined,
      overrideIncludeMyPublicProjects = undefined,
      overrideAppliedRoleFilter = undefined,
      overridePage = undefined,
      overrideActiveTab = undefined,
    } = {}
  ) => {
    const pageToFetch = overridePage !== undefined ? overridePage : page;
    // Early return if no user is logged in - can't fetch projects without authentication
    if (!currentUser?.username) {
      setMyProjects([]);
      // Only set global loading to false if tags aren't also in their initial loading phase
      if (tagsInitialized) {
        setIsLoading(false);
      }
      return;
    }
    
    // Set loading state to show loading indicators in the UI
    setIsLoading(true);
    setError(null);
    if (pageToFetch === 0) {
      setMyProjects([]);
    }

    try {
      const endpointUrl = APIURLMaker("projects/getFilteredProjects");

      // ============================================================================
      // BUILD REQUEST BODY FOR API CALL
      // ============================================================================
      
      // Start with basic request body - the backend expects specific field names
      const finalTags = Array.isArray(overrideTags) ? overrideTags : selectedTags;
      const requestBody = {
        orgType: "PERSONAL", // Currently only personal projects are supported
        getUpdateInfo: false, // Don't fetch update logs in list view for better performance
        includeDeletedRecords: false, // Never show deleted projects
        scope: buildListScope(activeTab, overrideActiveTab),
      };
      
      // Add search fields if they have values
      // Use override values if provided, otherwise use context values
      const finalKeyword = overrideKeyword !== null ? overrideKeyword : searchByKeyword;
      const finalSearchByTitle = overrideSearchByTitle !== null ? overrideSearchByTitle : searchByTitle;
      const finalSearchByDescription = overrideSearchByDescription !== null ? overrideSearchByDescription : searchByDescription;
      const finalSearchByTag = overrideSearchByTag !== null ? overrideSearchByTag : searchByTag;
      
      // Tag search:
      // - If a free-text tag search is present (finalSearchByTag), use it as the sole tag filter.
      // - Otherwise, fall back to selectedTags from the sidebar (if any).
      let requestTags = finalTags.length > 0 ? finalTags : [];
      if (finalSearchByTag && finalSearchByTag.trim()) {
        requestTags = [finalSearchByTag.trim()];
      }
      if (requestTags.length > 0) {
        requestBody.tags = requestTags;
      }
      
      // Keyword search (broad search across all fields)
      if (finalKeyword && finalKeyword.trim()) {
        requestBody.keyword = finalKeyword.trim();
      }
      
      // Precision search fields (backend supports exact matching)
      if (finalSearchByTitle.trim()) {
        requestBody.title = finalSearchByTitle.trim(); // Backend expects 'title' not 'name'
      }
      if (finalSearchByDescription.trim()) {
        requestBody.desc = finalSearchByDescription.trim(); // Backend expects 'desc' not 'description'
      }
      
      // ============================================================================
      // DATE FILTERING LOGIC
      // ============================================================================
      
      // Handle date filtering - use override values if provided, otherwise use state values
      const fromDay = overrideQueryFromDay !== null ? overrideQueryFromDay : queryFromDay;
      const toDay = overrideQueryToDay !== null ? overrideQueryToDay : queryToDay;
      
      // Add date filters to request if they exist
      // Backend expects LocalDate in ISO format: "YYYY-MM-DD" (e.g., "2026-01-19")
      // Spring Boot should automatically parse this to LocalDate
      if (fromDay) {
        requestBody.queryFromDay = fromDay; // Format: "2026-01-19" (ISO date string)
      }
      if (toDay) {
        requestBody.queryToDay = toDay; // Format: "2026-01-19" (ISO date string)
      }
      
      // ============================================================================
      // STATUS FILTERING LOGIC
      // ============================================================================
      // Only send statusList when user has deselected at least one status (exclusion logic).
      // Backend already excludes DELETED, so we don't send statusList when "all" are selected.
      const finalStatuses = Array.isArray(overrideStatuses) ? overrideStatuses : selectedStatuses;
      const allNonDeletedStatuses = getNonDeletedStatuses(PROJECT_STATUSES);
      const statusList = buildStatusListForRequest(finalStatuses, allNonDeletedStatuses);
      if (statusList) {
        requestBody.statusList = statusList;
      }

      // ============================================================================
      // RATING FILTERING LOGIC (minimum-threshold)
      // Backend expects: rating: { rating: <minRating> }
      // ============================================================================
      const finalMinRating = overrideMinRating !== undefined ? overrideMinRating : selectedMinRating;
      const shouldApplyRatingFilter = !noRatingFilterApplied && finalMinRating !== null;
      if (shouldApplyRatingFilter) {
        requestBody.rating = { rating: finalMinRating };
      }

      // ============================================================================
      // INCLUDE MY PUBLIC PROJECTS (backend field: includeMyPublicRecords)
      // ============================================================================
      const finalIncludeMyPublic = overrideIncludeMyPublicProjects !== undefined ? overrideIncludeMyPublicProjects : includeMyPublicProjects;
      requestBody.includeMyPublicRecords = finalIncludeMyPublic;

      // ============================================================================
      // ROLE FILTERING LOGIC → userRoleSet (backend field name)
      // Only send when user has applied role filter via "Filter By Role" button.
      // ============================================================================
      const roleSet = overrideAppliedRoleFilter !== undefined ? overrideAppliedRoleFilter : appliedRoleFilter;
      if (roleSet != null && roleSet.length > 0) {
        requestBody.userRoleSet = roleSet;
      }

      const parsed = await fetchPostWithEmptyPageRetry({
        endpointUrl,
        requestBody,
        pageToFetch,
        pageSize,
      });

      const apiProjects = parsed.items
        .map((project) => transformRecordFromBackend(project, "project", currentUser))
        .filter(Boolean);

      setMyProjects(apiProjects);
      applyPagedMeta(parsed);

    } catch (err) {
      // ============================================================================
      // ERROR HANDLING
      // ============================================================================
      
      console.error("Error fetching projects for display:", err);
      console.error("Error response data:", err.response?.data);
      console.error("Error status:", err.response?.status);
      
      setError("Failed to load projects for display.");
      setMyProjects([]);
      applyPagedMeta({
        items: [],
        page: 0,
        size: pageSize,
        totalElements: 0,
        totalPages: 0,
        hasNext: false,
        hasPrevious: false,
      });
    } finally {
      // Always turn off loading state, regardless of success or failure
      setIsLoading(false);
      setHasLoadedListOnce(true);
    }
  }, [
    currentUser,
    activeTab,
    tagsInitialized,
    selectedTags,
    searchByTag,
    searchByKeyword,
    searchByTitle,
    searchByDescription,
    queryFromDay,
    queryToDay,
    selectedStatuses,
    noRatingFilterApplied,
    selectedMinRating,
    includeMyPublicProjects,
    appliedRoleFilter,
    page,
    pageSize,
    applyPagedMeta,
  ]);

  // ============================================================================
  // PROJECT STATE MANAGEMENT FUNCTIONS
  // ============================================================================
  
  // Function to add a newly created project to the state
  const prependNewProject = useCallback(
    (newProjectData) => {
      // Add the new project to the beginning of the projects list
      setMyProjects((prevProjects) => [newProjectData, ...prevProjects]);
      
      // Update the tag list if the new project has tags that don't exist yet
      if (newProjectData.tags && newProjectData.tags.length > 0) {
        addTags(newProjectData.tags);
      }
    },
    [addTags]
  );

  // Function to remove a project from the local state
  const removeProjectFromState = useCallback((projectId) => {
    // Remove project from main projects list
    setMyProjects((prevProjects) => prevProjects.filter((p) => p.id !== projectId));
  }, []);

  // Function to update project status in local state without API call
  const updateProjectStatusInState = useCallback((projectId, newStatus) => {
    // Update the project in the main projects list
    setMyProjects((prevProjects) => 
      prevProjects.map(p => 
        p.id === projectId 
          ? { ...p, status: newStatus }
          : p
      )
    );
  }, []);

  // Function to replace a project in local state without any API call.
  // Useful for FE-only updates like rating submissions.
  const upsertProjectInState = useCallback((updatedProject) => {
    if (!updatedProject?.id) return;
    setMyProjects((prevProjects) => {
      const exists = prevProjects.some((p) => p.id === updatedProject.id);
      if (exists) {
        return prevProjects.map((p) => (p.id === updatedProject.id ? updatedProject : p));
      }
      return [updatedProject, ...prevProjects];
    });
  }, []);

  const applyIncomingProjectUpdate = useCallback(
    (payload) => {
      handleIncomingRecordUpdate({
        payload,
        parsePayload: parseProjectSsePayload,
        getBackendRecord: (parsed) => parsed.backendProject,
        itemType: "project",
        currentUser,
        upsertInList: (transformed, capturePrevious) => {
          setMyProjects((prevProjects) => {
            const existingIndex = prevProjects.findIndex(
              (project) => project.id === transformed.id
            );
            if (existingIndex === -1) {
              return [transformed, ...prevProjects];
            }
            capturePrevious(prevProjects[existingIndex]);
            return prevProjects.map((project) =>
              project.id === transformed.id ? transformed : project
            );
          });
        },
        onTagsDiscovered: addTags,
        scheduleHighlight: scheduleProjectHighlight,
      });
    },
    [currentUser, addTags, scheduleProjectHighlight]
  );

  const createProjectSubscription = useCallback(
    (projectId, userId, onUpdated) =>
      subscribeToProjectUpdates({
        projectId,
        userId,
        onProjectUpdated: onUpdated,
        onStatus: () => {},
        onError: () => {},
      }),
    []
  );

  const {
    start: startProjectRealtime,
    stop: stopProjectRealtime,
    stopAll: stopAllProjectRealtime,
  } = useManagedRealtimeSubscriptions(createProjectSubscription);

  const startProjectRealtimeWithUser = useCallback(
    (projectId) => {
      const userId = currentUser?.id || sessionStorage.getItem("userId");
      if (!userId) return;
      startProjectRealtime(projectId, applyIncomingProjectUpdate, userId);
    },
    [currentUser, startProjectRealtime, applyIncomingProjectUpdate]
  );

  // ============================================================================
  // PROJECT UPDATE FUNCTION
  // ============================================================================
  
  // Update an existing project with new data
  const updateProject = useCallback(async (updatedProjectData, updateComment = null) => {
    try {
      // Find the original project to preserve fields not in the edit form
      let originalProject = myProjects.find(p => p.id === updatedProjectData.id);
      if (!originalProject) {
        throw new Error("Original project not found in state");
      }
      
      // CRITICAL: Ensure originalProject has usernames (not IDs)
      // This prevents stale IDs from being used
      if (originalProject.accessMap) {
        originalProject = {
          ...originalProject,
          accessMap: normalizeAccessMap(originalProject.accessMap)
        };
      }
      
      // CRITICAL: Always use accessMap from updatedProjectData if it exists
      // This ensures we use the latest form data with usernames, not stale IDs
      // IMPORTANT: Don't merge - use updatedProjectData.accessMap directly to avoid ID contamination
      let finalAccessMap = updatedProjectData.accessMap;
      
      if (finalAccessMap) {
        // Normalize to ensure usernames (not IDs)
        finalAccessMap = normalizeAccessMap(finalAccessMap);
      } else {
        // Fallback: use normalized original
        finalAccessMap = normalizeAccessMap(originalProject.accessMap);
      }
      
      // Merge original data with updated data, but use the normalized accessMap
      const mergedProject = {
        ...originalProject,
        ...updatedProjectData,
        accessMap: finalAccessMap, // CRITICAL: Use the normalized accessMap
      };
      
      // Use utility function to transform to backend format
      const backendProject = transformItemToBackend(mergedProject, 'project');
      
      // Ensure accessMap.userRoles have a consistent structure before sending.
      // IMPORTANT: Backend update endpoint only relies on `username` (it resolves username → userId).
      // To avoid confusion, we do NOT send a fake `userId` that just repeats the username.
      if (backendProject.accessMap?.userRoles) {
        backendProject.accessMap.userRoles = backendProject.accessMap.userRoles
          .filter(ur => ur && (ur.userId || ur.username) && ur.roletype)
          .map(ur => {
            const userId = ur.userId ? String(ur.userId).trim() : '';
            const usernameFromField = ur.username ? String(ur.username).trim() : '';
            // If we don't have an explicit username but userId is not a MongoDB ObjectId,
            // treat userId as the username (frontend often stores usernames in userId).
            const isObjectId = userId && /^[a-f0-9]{24}$/i.test(userId);
            const username = usernameFromField || (!isObjectId ? userId : '');
            
            return {
              // Only send username + roletype; omit userId for updates unless backend later
              // decides to use it. This keeps the contract clear: username is authoritative.
              username: username || undefined,
              roletype: String(ur.roletype).trim()
            };
          })
          // Backend requires username to resolve; drop any entries where we still
          // couldn't determine a username (these should be fixed at the data/backend level).
          .filter(ur => ur.username && ur.roletype);
      }
      
      // Ensure orgDetails is always present with proper values (transformItemToBackend handles this, but preserve existing if present)
      if (!backendProject.orgDetails && originalProject.orgDetails) {
        backendProject.orgDetails = {
          orgName: originalProject.orgDetails.orgName || "",
          groupName: originalProject.orgDetails.groupName || "",
          dept: originalProject.orgDetails.dept || "",
          orgtype: originalProject.orgDetails.orgtype || "PERSONAL"
        };
      }
      
      // Create updateInfo object; backend resolves updatedByUsername to userId
      const updateInfo = {
        reason: "METADATA_UPDATE",
        comment: updateComment?.trim() || "",
        updatedByUsername: currentUser?.username || ""
      };
      
      // Structure the request according to ProjectUpdateRequest model
      const requestBody = {
        updateInfo: updateInfo,
        updatedProject: backendProject
      };
      
      // Make API call
      const url = APIURLMaker("projects/updateProject");
      const response = await axios.post(url, requestBody);
      
      // Transform API response back to frontend format using utility function
      const finalProjectState = transformItemFromBackend(response.data, 'project');
      
      // Preserve original createdAt if it exists (transformItemFromBackend handles this, but ensure we keep it)
      if (originalProject.createdAt && !finalProjectState.createdAt) {
        finalProjectState.createdAt = originalProject.createdAt;
      }
      
      // Normalize accessMap for consistent structure (may still contain IDs for userId)
      if (finalProjectState.accessMap) {
        finalProjectState.accessMap = normalizeAccessMap(finalProjectState.accessMap);
      }
      
      // Update the project in the projects list
      setMyProjects(prevProjects => {
        const updatedProjects = prevProjects.map(p => (p.id === finalProjectState.id ? finalProjectState : p));
        return updatedProjects;
      });
      
      return finalProjectState; // Return the updated project so callers can update their local state
    } catch (error) {
      console.error("Error updating project:", error);
      console.error("Error response:", error.response?.data);
      
      // Re-throw the error so the calling component can handle it
      throw error;
    }
  }, [myProjects, currentUser]);

  // ============================================================================
  // USEEFFECT HOOKS FOR DATA LOADING
  // ============================================================================
  
  // Effect for initial data load: one fetch with default filters, then mark ready for refetch-on-filter-change.
  // Tags are no longer loaded here; they are loaded on demand when Create/Edit modal opens (see ProjectTagContext).
  useEffect(() => {
    if (hasPermission && currentUser?.username && !tagsInitialized && isBootstrapReady) {
      if (!skipInitialListFetch) {
        fetchFilteredProjects();
      }
      markTagsAsInitialized();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasPermission, currentUser, tagsInitialized, isBootstrapReady]);

  // Refetch when switching tabs so Discovery uses scope EXTERNAL from the backend.
  useEffect(() => {
    if (hasPermission && currentUser?.username && tagsInitialized) {
      fetchFilteredProjects(null, null, null, null, null, { overridePage: 0 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const goToNextPage = useCallback(() => {
    if (!pagination.hasNext) return;
    fetchFilteredProjects(null, null, null, null, null, { overridePage: page + 1 });
  }, [pagination.hasNext, page, fetchFilteredProjects]);

  const goToPreviousPage = useCallback(() => {
    if (!pagination.hasPrevious) return;
    fetchFilteredProjects(null, null, null, null, null, { overridePage: page - 1 });
  }, [pagination.hasPrevious, page, fetchFilteredProjects]);

  useEffect(() => {
    return () => {
      stopAllProjectRealtime();
      clearAllProjectHighlights();
    };
  }, [stopAllProjectRealtime, clearAllProjectHighlights]);

  // No refetch effect for search/status/visibility/role: all of them are triggered by their
  // handlers (Search button, handleStatusesChange, handleVisibilitiesChange, handleProjectScopeChange,
  // handleClearAllFilters). Having searchByTitle/searchByDescription in deps caused a second fetch
  // when the Search button updated state and then the effect ran.

  // Display derivation: normalize and tab filter (logic in useDisplayProjects hook)
  const getDisplayProjects = useDisplayProjects(myProjects, activeTab, currentUser);

  const isListPending = useMemo(() => {
    if (!currentUser?.username || !hasPermission) {
      return false;
    }
    return !isBootstrapReady || isLoading || !hasLoadedListOnce;
  }, [currentUser?.username, hasPermission, isBootstrapReady, isLoading, hasLoadedListOnce]);

  // ============================================================================
  // CONTEXT VALUE CREATION
  // ============================================================================
  
  // Create the context value that will be provided to all child components
  const contextValue = useMemo(() => ({
    // ============================================================================
    // USER AND AUTHENTICATION DATA
    // ============================================================================
    userData: userProfile,           // Current user's profile information
    userType,                        // Type of user (currently only "personal")
    setUserType,                     // Function to update user type
    
    // ============================================================================
    // PROJECT DATA
    // ============================================================================
    myProjects,                      // Raw projects data from API

    // ============================================================================
    // LOADING AND ERROR STATES
    // ============================================================================
    isLoading,                       // Global loading state
    isListPending,                   // True until first list fetch completes (incl. preset bootstrap)
    error,                           // Current error message
    
    // ============================================================================
    // API FUNCTIONS
    // ============================================================================
    fetchFilteredProjects,           // Function to fetch projects from API
    removeProjectFromState,          // Function to remove project from state
    prependNewProject,               // Function to add new project to state
    updateProject,                   // Function to update project via API
    updateProjectStatusInState,      // Function to update project status in state
    upsertProjectInState,            // Function to replace a project in state
    startProjectRealtime: startProjectRealtimeWithUser,
    stopProjectRealtime,
    projectFieldHighlightsById,      // Temporary field highlights from realtime delta payloads
    projectFieldHighlightDetailsById,// Fine-grained field highlight details from realtime updates
    getDisplayProjects,              // Function to get currently displayed projects
    pagination,
    goToNextPage,
    goToPreviousPage,
    resetPage,
  }), [
    userProfile,
    userType,
    myProjects,
    isLoading,
    isListPending,
    error,
    fetchFilteredProjects,
    removeProjectFromState,
    prependNewProject,
    updateProject,
    updateProjectStatusInState,
    upsertProjectInState,
    startProjectRealtimeWithUser,
    stopProjectRealtime,
    projectFieldHighlightsById,
    projectFieldHighlightDetailsById,
    getDisplayProjects,
    pagination,
    goToNextPage,
    goToPreviousPage,
    resetPage,
  ]);

  return (
    <ProjectDataContext.Provider value={contextValue}>
      {children}
    </ProjectDataContext.Provider>
  );
};
